/**
 * @element qds-action-panel
 *
 * A compact action-tile panel for hub pages and launch surfaces. It renders a
 * title, optional description, and a responsive grid of large CTA tiles.
 *
 * @attr {string} title - Panel title
 * @attr {string} description - Optional supporting copy
 * @attr {number} columns - Preferred desktop column count
 * @attr {string} items - JSON action-tile array
 * @property {object} block - Content block model
 * @property {object} config - Action-panel configuration
 * @property {object} context - Render context
 */

import { adoptPrimeIconsStyles, loadBaseCSS } from '../shared/button-utils.js';
import { hydrateQdsLucideIcons, renderQdsIcon } from '../shared/icon-utils.js';
import { applyTypographyStyle } from '../shared/typography-utils.js';
import { sanitizeUrl } from '../shared/url-utils.js';
import type { QdsContentBlockInstance } from '../../types/content.js';
import type { QdsRenderContext } from '../../types/blocks.js';

loadBaseCSS();

interface ActionPanelItem {
  label?: string;
  href?: string;
  target?: string;
  /** A qds-icon-picker value such as `prime:pi-folder` or `lucide:folder`. */
  icon?: string;
  iconSrc?: string;
  iconAlt?: string;
  actionKind?: 'forward' | 'external' | 'download' | string;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function toNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
}

function toStringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function parseItems(value: unknown): ActionPanelItem[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function renderTrailingIcon(actionKind: string): string {
  switch (actionKind) {
    case 'download':
      return `
        <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M10 2.75a.75.75 0 0 1 .75.75v7.19l2.22-2.22a.75.75 0 1 1 1.06 1.06l-3.5 3.5a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06l2.22 2.22V3.5a.75.75 0 0 1 .75-.75ZM4.5 14.25a.75.75 0 0 1 .75.75v.5c0 .14.11.25.25.25h9a.25.25 0 0 0 .25-.25V15a.75.75 0 0 1 1.5 0v.5c0 .97-.78 1.75-1.75 1.75h-9A1.75 1.75 0 0 1 3.75 15.5V15a.75.75 0 0 1 .75-.75Z" />
        </svg>
      `;

    case 'external':
      return `
        <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M11.75 3.75a.75.75 0 0 1 0-1.5h4.5c.41 0 .75.34.75.75v4.5a.75.75 0 0 1-1.5 0V4.81l-6.97 6.97a.75.75 0 1 1-1.06-1.06l6.97-6.97h-2.69Z" />
          <path d="M5 4.75A2.25 2.25 0 0 0 2.75 7v8A2.25 2.25 0 0 0 5 17.25h8A2.25 2.25 0 0 0 15.25 15v-3a.75.75 0 0 0-1.5 0v3a.75.75 0 0 1-.75.75H5a.75.75 0 0 1-.75-.75V7A.75.75 0 0 1 5 6.25h3a.75.75 0 0 0 0-1.5H5Z" />
        </svg>
      `;

    default:
      return `
        <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M6.47 4.47a.75.75 0 0 1 1.06 0l4.99 5-4.99 5a.75.75 0 1 1-1.06-1.06L10.4 10 6.47 6.03a.75.75 0 0 1 0-1.06Z" />
        </svg>
      `;
  }
}

export class QdsActionPanel extends HTMLElement {
  private _block?: QdsContentBlockInstance;
  private _config: Record<string, unknown> = {};
  private _context?: QdsRenderContext;

  static get observedAttributes(): string[] {
    return ['title', 'description', 'columns', 'items'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    switch (name) {
      case 'title':
        this._config.title = newValue || '';
        break;
      case 'description':
        this._config.description = newValue || '';
        break;
      case 'columns':
        this._config.columns = newValue || '2';
        break;
      case 'items':
        this._config.items = newValue || '[]';
        break;
      default:
        break;
    }

    this.render();
  }

  get block(): QdsContentBlockInstance | undefined {
    return this._block;
  }

  set block(value: QdsContentBlockInstance | undefined) {
    this._block = value;
    this.render();
  }

  get config(): Record<string, unknown> {
    return this._config;
  }

  set config(value: Record<string, unknown> | undefined) {
    this._config = value ? { ...value } : {};
    this.render();
  }

  get context(): QdsRenderContext | undefined {
    return this._context;
  }

  set context(value: QdsRenderContext | undefined) {
    this._context = value;
    this.render();
  }

  private _resolveConfig(): Record<string, unknown> {
    return {
      ...(this._block?.config || {}),
      ...this._config
    };
  }

  private _resolveLink(value: string): string {
    if (!value) {
      return '';
    }

    const resolved = this._context?.resolveLink ? this._context.resolveLink(value) : value;
    return sanitizeUrl(resolved) || '';
  }

  private _resolveAssetUrl(value: string): string {
    if (!value) {
      return '';
    }

    return this._context?.resolveAssetUrl ? this._context.resolveAssetUrl(value) : value;
  }

  private _renderItem(item: ActionPanelItem): string {
    const label = toStringValue(item.label, 'Open');
    const href = this._resolveLink(toStringValue(item.href));
    const target = toStringValue(item.target, '_self') || '_self';
    const safeTarget = escapeHtml(target);
    const iconSrc = this._resolveAssetUrl(toStringValue(item.iconSrc));
    const namedIcon = renderQdsIcon(item.icon, 'action-panel__tile-icon');
    const iconAlt = escapeHtml(toStringValue(item.iconAlt, `${label} icon`));
    const rel = safeTarget === '_blank' ? ' rel="noopener noreferrer"' : '';
    const trailingIcon = renderTrailingIcon(toStringValue(item.actionKind, 'forward'));
    const tileBody = `
      ${namedIcon || (iconSrc ? `<img class="action-panel__tile-icon" src="${escapeHtml(iconSrc)}" alt="${iconAlt}">` : '<span class="action-panel__tile-icon action-panel__tile-icon--empty" aria-hidden="true"></span>')}
      <span class="action-panel__tile-label">${escapeHtml(label)}</span>
      <span class="action-panel__tile-trailing" aria-hidden="true">${trailingIcon}</span>
    `;

    if (!href) {
      return `<div class="action-panel__tile action-panel__tile--disabled">${tileBody}</div>`;
    }

    return `
      <a class="action-panel__tile" href="${escapeHtml(href)}" target="${safeTarget}"${rel}>
        ${tileBody}
      </a>
    `;
  }

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const config = this._resolveConfig();
    const title = toStringValue(config.title);
    const description = toStringValue(config.description);
    const columns = Math.max(1, Math.min(4, toNumber(config.columns, 2)));
    const items = parseItems(config.items);

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          height: var(--qds-action-panel-host-height, auto);
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        .action-panel {
          display: grid;
          gap: 0;
          width: 100%;
          min-height: var(--qds-action-panel-min-height, auto);
          max-width: var(--qds-action-panel-max-width, 1004px);
          margin: var(--qds-action-panel-margin, 0 auto);
          padding: var(--qds-action-panel-padding, clamp(24px, 4vw, 50px));
          border-style: solid;
          border-color: var(--qds-action-panel-border-color, #ffffff);
          border-top-width: var(--qds-action-panel-border-top-width, var(--qds-action-panel-border-width, 3px));
          border-right-width: var(--qds-action-panel-border-right-width, var(--qds-action-panel-border-width, 3px));
          border-bottom-width: var(--qds-action-panel-border-bottom-width, var(--qds-action-panel-border-width, 3px));
          border-left-width: var(--qds-action-panel-border-left-width, var(--qds-action-panel-border-width, 3px));
          border-radius: var(--qds-action-panel-border-radius, 8px);
          background: linear-gradient(112deg, rgba(255, 255, 255, 0.6) 27.27%, rgba(255, 255, 255, 0.8) 102.04%);
          backdrop-filter: blur(12px);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
          box-sizing: border-box;
          align-content: start;
        }

        .action-panel__header {
          display: grid;
          gap: 0;
        }

        .action-panel__title {
          margin: 0;
          color: #000000;
          font-size: 24px;
          font-weight: 500;
          line-height: 1.2;
        }

        .action-panel__description {
          margin: 24px 0 0;
          color: #6c6c6c;
          font-size: 16px;
          font-weight: 400;
          line-height: 24px;
          max-width: 70ch;
        }

        .action-panel__grid {
          display: grid;
          width: 100%;
          grid-template-columns: repeat(${columns}, minmax(0, 1fr));
          gap: clamp(16px, 3vw, 36px);
          justify-items: stretch;
          align-items: stretch;
          padding-top: 36px;
        }

        .action-panel__tile {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          width: 100%;
          min-width: 0;
          min-height: 44px;
          padding: 10.5px 17.5px;
          border: 1px solid #393939;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.5);
          color: #393939;
          text-decoration: none;
          transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          box-sizing: border-box;
        }

        .action-panel__tile:hover {
          background: rgba(57, 57, 57, 0.08);
          box-shadow: 0 1px 4px rgba(32, 33, 36, 0.12);
        }

        .action-panel__tile:focus-visible {
          outline: 2px solid var(--input-focus-border, #4285f4);
          outline-offset: 2px;
        }

        .action-panel__tile--disabled {
          opacity: 0.55;
          cursor: default;
        }

        .action-panel__tile-icon {
          width: 21px;
          height: 21px;
          object-fit: contain;
          flex: 0 0 auto;
        }

        .action-panel__tile-icon--empty {
          width: 21px;
          height: 21px;
        }

        .action-panel__tile-label {
          font-family: var(--qds-action-panel-button-font-family, 'Roboto', var(--font-family-primary, 'IBM Plex Sans', sans-serif));
          font-size: 14px;
          font-weight: 600;
          line-height: 21px;
          color: inherit;
          text-align: center;
          flex: 1 1 auto;
          min-width: 0;
          overflow-wrap: anywhere;
        }

        .action-panel__tile-trailing {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          color: inherit;
          flex: 0 0 auto;
        }

        .action-panel__tile-trailing svg {
          width: 18px;
          height: 18px;
          fill: currentColor;
        }

        .action-panel__empty {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          font-size: 14px;
        }

        @media (max-width: 1024px) {
          .action-panel {
            max-width: 100%;
            padding: 32px 24px;
          }

          .action-panel__grid {
            grid-template-columns: repeat(1, minmax(0, 1fr));
          }

          .action-panel__tile {
            width: 100%;
          }
        }
      </style>
      <section class="action-panel" part="panel">
        <div class="action-panel__header" part="header">
          ${title ? `<h2 class="action-panel__title" part="title">${escapeHtml(title)}</h2>` : ''}
          ${description ? `<p class="action-panel__description" part="description">${escapeHtml(description)}</p>` : ''}
        </div>
        ${items.length > 0
          ? `<div class="action-panel__grid" part="grid">${items.map((item) => this._renderItem(item)).join('')}</div>`
          : '<p class="action-panel__empty">No actions configured yet.</p>'}
      </section>
    `;

    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.action-panel__title'), config.titleFont);
    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.action-panel__description'), config.descriptionFont);
    void adoptPrimeIconsStyles(this.shadowRoot);
    hydrateQdsLucideIcons(this.shadowRoot);
  }
}

if (!customElements.get('qds-action-panel')) {
  customElements.define('qds-action-panel', QdsActionPanel);
}

export default QdsActionPanel;
